import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CalculatorForm from "../components/CalculatorForm";
import Loading from "../components/Loading";
import Result from "../components/Result";
import NewReportActions from "../components/NewReportActions"; // Import the new component
import { calculateEmissions, fetchCalculationByEmissionId } from "../services/emissionsApi";

const HomePage = () => {
  const location = useLocation();
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [cabinClass, setCabinClass] = useState("business");
  const [tripType, setTripType] = useState("One Way");
  const [operatingCarrierCode, setOperatingCarrierCode] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [departureDate, setDepartureDate] = useState(new Date().toISOString().split("T")[0]);
  const [email, setEmail] = useState("");
  const [emissions, setEmissions] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [isValidEmissionId, setIsValidEmissionId] = useState(false);
  const [finalEmissionId, setFinalEmissionId] = useState(null);

  // New state for terms acceptance
  const [isTermsAccepted, setTermsAccepted] = useState(false);

  // Extract emissionId from query parameters
  const queryParams = new URLSearchParams(location.search);
  const emissionId = queryParams.get("emissionId");

  // Fetch data by emissionId if it exists
  useEffect(() => {
    const fetchData = async () => {
      if (emissionId) {
        setLoading(true);
        try {
          const data = await fetchCalculationByEmissionId(emissionId);
          setIsValidEmissionId(true);
          setFinalEmissionId(emissionId);
          setDeparture(data.request.origin || "");
          setArrival(data.request.destination || "");
          setPassengers(data.request.passengers || 1);
          setCabinClass(data.request.cabinClass || "all");
          setTripType(data.request.tripType || "One Way");
          setEmail(data.email || "");
          setTermsAccepted(data.isTermsAccepted || false);
          setOperatingCarrierCode(data.flightDetails.operatingCarrierCode || "");
          setFlightNumber(data.flightDetails.flightNumber || "");
          setDepartureDate(data.request.departureDate || new Date().toISOString().split("T")[0]);

          setEmissions(data);
          // const status = data.status;
          // if (status === "Calculated") {
          //   setEmissions(data);
          // }
          // if (status === "Pending" || status === "Failed") {
          //   setError(data.response?.error);
          // }
        } catch (err) {
          setError("Invalid or Expired EmissionId. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [emissionId]);

  const handleCalculate = async () => {
    if (!departure || !arrival || !passengers || !cabinClass || !tripType || !email || !isTermsAccepted) {
      setError("All fields are required, including email.");
      return;
    }

    if (departure === arrival) {
      setError("Departure Airport and Arrival Airport cannot be the same.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setApiLoaded(true);
    setEmissions(null);
    setError(null);

    try {
      const result = await calculateEmissions({
        departure,
        arrival,
        passengers,
        cabinClass,
        tripType,
        operatingCarrierCode,
        flightNumber,
        departureDate,
        email,
        emissionId: isValidEmissionId ? emissionId : finalEmissionId,
        isTermsAccepted,
      });
      if (result.success) {
        setEmissions(result.data);
        setFinalEmissionId(result.data?.emissionId);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to calculate emissions. Please try again.");
    } finally {
      setApiLoaded(false);
    }
  };

  const isPaid = emissions?.paymentId?.status === "Paid";

  return (
    <div className="container mt-5">
      {loading && <Loading message="Loading ...." />}
      {!loading && (
        <CalculatorForm
          isDisabled={isPaid}
          departure={departure}
          setDeparture={setDeparture}
          arrival={arrival}
          setArrival={setArrival}
          passengers={passengers}
          setPassengers={setPassengers}
          cabinClass={cabinClass}
          setCabinClass={setCabinClass}
          tripType={tripType}
          setTripType={setTripType}
          operatingCarrierCode={operatingCarrierCode}
          setOperatingCarrierCode={setOperatingCarrierCode}
          flightNumber={flightNumber}
          setFlightNumber={setFlightNumber}
          departureDate={departureDate}
          setDepartureDate={setDepartureDate}
          email={email}
          setEmail={setEmail}
          handleCalculate={handleCalculate}
          isTermsAccepted={isTermsAccepted} // Pass the status to the child
          setTermsAccepted={setTermsAccepted} // Pass the setter to the child
        />
      )}
      {apiLoaded && <Loading message="Calculating emissions..." />}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {emissions && <Result data={emissions} />}

      {/* Use the new component */}
      <NewReportActions isPaid={isPaid} />
    </div>
  );
};

export default HomePage;